/**
 * Consent helper — single source of truth for whether analytics is allowed.
 *
 * Memory Lane uses Google Analytics 4 (gtag.js) and Microsoft Clarity. Both
 * are first-party tools that we explain in /privacy and /cookies.
 *
 * Compliance model:
 *   - Users in the EU / EEA / UK or with GPC enabled get an OPT-IN
 *     experience: defaults are denied, banner asks them to accept.
 *   - Users elsewhere (US, Canada, LATAM, APAC, etc.) get an OPT-OUT
 *     experience: analytics auto-grant on first visit, banner is a
 *     short cookie notice with a one-click decline. This matches
 *     CCPA/CPRA "legitimate interest" practice and is why the founder
 *     was previously seeing only her own + the platform's recordings —
 *     real US caregivers were never clicking "Accept" so Clarity
 *     never booted for them.
 *
 * Storage:
 *   - One key in localStorage: `ml_consent_v1` shaped
 *     {analytics, clarity, ts, decided}.
 *
 * Internal traffic:
 *   - /lib/internalTraffic.js owns the "is this our own team" check.
 *     This module asks it before booting Clarity or granting GA4
 *     consent. Internal sessions are always denied, regardless of
 *     stored consent.
 *
 * Wiring:
 *   - GA4 is bridged through Google Consent Mode v2.
 *   - Clarity is bootstrapped lazily — we only inject its <script>
 *     tag after consent is granted.
 *   - Banner subscribes to changes via the small event-emitter below.
 */

import { isInternalUser, isInternalUserStrict } from "@/lib/internalTraffic";

const STORAGE_KEY = "ml_consent_v1";
const CONSENT_EVENT = "ml-consent-change";

export const DEFAULT_CONSENT = { analytics: false, clarity: false, ts: 0, decided: false };

// ---- Geo / GPC detection --------------------------------------------------

function readGpc() {
    if (typeof navigator === "undefined") return false;
    return navigator.globalPrivacyControl === true;
}

/**
 * Best-effort timezone-based check for whether the visitor is likely in
 * the EU/EEA/UK. Used only to decide the *default* consent state for a
 * brand-new visitor — once they make a decision (Accept / Decline /
 * Customize) it's persisted explicitly and this check is bypassed.
 *
 * We intentionally avoid IP geolocation (latency + privacy). Timezone
 * leaks far less PII and is accurate enough for "did this person come
 * from a GDPR jurisdiction?" — the default-deny side is the safe side
 * to fall back to if we're wrong.
 */
function isLikelyGdprRegion() {
    if (typeof Intl === "undefined") return false;
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
        if (!tz) return false;
        // Europe/* covers the EU, UK, and EEA in IANA's tz database.
        // Atlantic/Azores + Atlantic/Madeira are Portuguese territories.
        // Asia/Nicosia + Europe/Istanbul cover Cyprus + the Turkish part
        // of the European Customs Union edge cases. Add more as needed.
        if (tz.startsWith("Europe/")) return true;
        if (tz === "Atlantic/Azores" || tz === "Atlantic/Madeira") return true;
        if (tz === "Atlantic/Canary" || tz === "Atlantic/Reykjavik") return true;
        return false;
    } catch {
        return false;
    }
}

// ---- Stored consent -------------------------------------------------------

export function getConsent() {
    if (typeof window === "undefined") return DEFAULT_CONSENT;

    // Stored decision wins everything else.
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            return { ...DEFAULT_CONSENT, ...parsed, decided: true };
        }
    } catch {
        /* corrupted blob — fall through to default */
    }

    // No prior decision yet.
    //
    // 1. GPC users have already spoken (deny).
    if (readGpc()) {
        return { ...DEFAULT_CONSENT, decided: true, ts: Date.now() };
    }

    // 2. EU/EEA/UK visitors → opt-IN: keep defaults denied, banner
    //    will show and ask them.
    if (isLikelyGdprRegion()) {
        return { ...DEFAULT_CONSENT };
    }

    // 3. Everyone else (US, rest of world) → opt-OUT: auto-grant.
    //    The banner still renders as a short cookie notice with a
    //    one-click "Decline" so users can change their mind.
    return {
        analytics: true,
        clarity: true,
        ts: Date.now(),
        decided: false,            // banner still shows once
        autoGranted: true,         // marker used by the banner copy
    };
}

export function setConsent(next) {
    if (typeof window === "undefined") return;
    const value = {
        analytics: !!next.analytics,
        clarity: !!next.clarity,
        ts: Date.now(),
        decided: true,
    };
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
        /* private mode — ignore, we'll just re-ask next visit */
    }
    applyConsent(value);
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
}

export function acceptAll() {
    setConsent({ analytics: true, clarity: true });
}
export function declineAll() {
    setConsent({ analytics: false, clarity: false });
}

export function onConsentChange(handler) {
    if (typeof window === "undefined") return () => { };
    const wrapped = (e) => handler(e.detail || getConsent());
    window.addEventListener(CONSENT_EVENT, wrapped);
    return () => window.removeEventListener(CONSENT_EVENT, wrapped);
}

// ---- SDK plumbing ---------------------------------------------------------

/**
 * Push the consent decision into the actual analytics SDKs.
 * Safe to call repeatedly; calls are idempotent.
 *
 * Internal sessions are always forced to denied here regardless of
 * stored consent — even if the teammate previously clicked "Accept"
 * on their personal device, /admin sessions and admin role users won't
 * leak data into Clarity / GA4.
 */
export function applyConsent(value) {
    if (typeof window === "undefined") return;
    const v = value || getConsent();

    // Internal users / non-production hosts: hard-deny everything.
    const internal = isInternalUser();

    // ---- GA4 (Google Consent Mode v2) ---------------------------------
    if (typeof window.gtag === "function") {
        window.gtag("consent", "update", {
            analytics_storage: (!internal && v.analytics) ? "granted" : "denied",
            ad_storage: "denied",            // we never use ads
            ad_user_data: "denied",
            ad_personalization: "denied",
        });
    }

    // ---- Microsoft Clarity --------------------------------------------
    // Strict check: the GA4 DebugView flag must not un-gate recording.
    const internalForClarity = isInternalUserStrict();
    if (!internalForClarity && v.clarity) {
        bootClarity();
    } else if (internalForClarity) {
        // If Clarity has already booted (e.g. user was anonymous and
        // then signed in as admin), stop it immediately.
        stopClarityIfRecording();
    }
}

/**
 * Apply the *default* consent state synchronously, before gtag.js has a
 * chance to set its first cookies. Called from index.js right after the
 * gtag stub is created. Internal sessions short-circuit so no Clarity
 * tag is ever injected on /admin / preview / localhost / teammate logins.
 */
export function applyDefaultConsent() {
    if (typeof window === "undefined") return;
    const v = getConsent();
    const internal = isInternalUser();
    if (typeof window.gtag === "function") {
        window.gtag("consent", "default", {
            analytics_storage: (!internal && v.analytics) ? "granted" : "denied",
            ad_storage: "denied",
            ad_user_data: "denied",
            ad_personalization: "denied",
            wait_for_update: 500,
        });
    }
    if (!isInternalUserStrict() && v.clarity) bootClarity();
}

// ---- Microsoft Clarity ----------------------------------------------------

let CLARITY_BOOTED = false;
const CLARITY_PROJECT_ID = "x9mna8uphh"; // existing Memory Lane project

/**
 * isAdminContext — legacy check kept for back-compat with code that
 * imports it. The full "internal traffic" decision now lives in
 * /lib/internalTraffic.js (isInternalUser) which is a strict superset
 * of this check.
 */
export function isAdminContext() {
    if (typeof window === "undefined") return false;
    const path = (window.location.pathname || "").toLowerCase();
    if (path.startsWith("/admin")) return true;
    try {
        const ctx = JSON.parse(window.localStorage.getItem("mlcn_ga_user_context") || "{}");
        if (ctx.user_type === "admin") return true;
    } catch { /* ignore */ }
    return false;
}

/**
 * Stop recording if Clarity is currently active (e.g. caregiver
 * navigated INTO /admin from a public page, or an internal teammate
 * logged in after Clarity already booted anonymously). Microsoft
 * Clarity exposes `clarity('stop')` for this exact use-case. Idempotent.
 */
export function stopClarityIfRecording() {
    if (typeof window === "undefined") return;
    if (typeof window.clarity === "function") {
        try { window.clarity("stop"); } catch { /* no-op */ }
    }
}

/**
 * Inject the Microsoft Clarity tag. Lives behind the consent gate +
 * the internal-traffic filter — never injected on /admin / preview /
 * localhost or for admin-role users.
 */
function bootClarity() {
    if (CLARITY_BOOTED) return;
    if (typeof window === "undefined" || typeof document === "undefined") return;
    if (isInternalUserStrict()) return; // never record internal sessions (debug flag does NOT bypass)
    if (isAdminContext()) return;  // belt + suspenders
    // Health-data route guard — if the caregiver's current URL is on a
    // page where PHI-adjacent content can be typed (chat, behavior,
    // wellness, etc.) we do NOT boot Clarity at all. See
    // HEALTH_DATA_ROUTES + enforceHealthRouteClarityPolicy below.
    if (CLARITY_SUPPRESSED_FOR_HEALTH) return;
    try {
        if (isHealthDataRoute(window.location?.pathname || "")) {
            CLARITY_SUPPRESSED_FOR_HEALTH = true;
            return;
        }
    } catch { /* fall through — a bad location object shouldn't block boot */ }
    CLARITY_BOOTED = true;
    (function (c, l, a, r, i, t, y) {
        c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
        t = l.createElement(r);
        t.async = 1;
        t.src = "https://www.clarity.ms/tag/" + i;
        y = l.getElementsByTagName(r)[0];
        y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", CLARITY_PROJECT_ID);
}

export { CLARITY_PROJECT_ID };

// ---- Health-data route exclusion -----------------------------------------
//
// Session replay (Clarity) captures full DOM. Even with input masking,
// the safest posture on pages where caregivers type PHI-adjacent
// content (chat with Lane, behavior notes, wellness/burnout notes,
// appointment prep questions, patient profile, My Story) is to not
// record at all. GA4 still fires — it only sees the route path — but
// Clarity is stopped on entry to these routes and refuses to boot
// while the user is on one.
//
// Keep this list in one place so the frontend + tests + `/security`
// documentation stay in sync.
export const HEALTH_DATA_ROUTES = [
    "/chat",
    "/behavior",
    "/wellness",
    "/burnout",
    "/appointment",
    "/care-team-summary",
    "/patient",
    "/my-story",
    "/intake",
    "/family",
    "/conversations",
];

export function isHealthDataRoute(pathname) {
    if (typeof pathname !== "string" || !pathname) return false;
    const p = pathname.toLowerCase();
    for (const r of HEALTH_DATA_ROUTES) {
        if (p === r || p.startsWith(r + "/") || p.startsWith(r + "?")) return true;
    }
    return false;
}

/**
 * Enforce the health-data-route policy for the CURRENT page.
 *
 *   - If the current route is a health-data route: hard-stop Clarity
 *     (if it happened to boot on a prior route) and flip a flag so
 *     bootClarity() refuses to inject the script.
 *   - If the current route is not a health-data route: clear the flag
 *     and allow Clarity to boot if consent was previously granted.
 *
 * Called from the GA4PageTracker on every route change so navigation
 * INTO a health page immediately stops recording, and navigation OUT
 * restores it on the next allowed page.
 */
let CLARITY_SUPPRESSED_FOR_HEALTH = false;
export function enforceHealthRouteClarityPolicy(pathname) {
    if (typeof window === "undefined") return;
    if (isHealthDataRoute(pathname)) {
        CLARITY_SUPPRESSED_FOR_HEALTH = true;
        stopClarityIfRecording();
        return;
    }
    if (CLARITY_SUPPRESSED_FOR_HEALTH) {
        CLARITY_SUPPRESSED_FOR_HEALTH = false;
        // Re-apply consent so Clarity can resume on the next allowed page
        // if the caregiver had previously accepted analytics.
        try {
            applyConsent();
        } catch { /* never block navigation on analytics */ }
    }
}
export function _isClaritySuppressedForHealth() {
    return CLARITY_SUPPRESSED_FOR_HEALTH;
}
